<template>
  <div class="v-stack gap-5 h-stretch">
    <div class="pane-left gap-3">
      <div class="h-stack gap-3">
        <label style="width: 40px">Client</label>
        <select class="fixed" v-model="selectedClient">
          <option value="all">All</option>
          <option
            v-for="client in clients"
            :key="client._id"
            :value="client._id"
          >
            {{ client.name }}
          </option>
        </select>
      </div>
      <div class="pane-right gap-3">
        <input type="text" placeholder="Type to search..." v-model="search" />
        <button @click="$router.push('/projects/add')" class="add">New</button>
      </div>
    </div>
    <div class="h-stack gap-3 h-start">
      <div class="h-stack gap-3" v-for="state in states" :key="state.name">
        <label
          >{{
            state.name.charAt(0).toUpperCase() + state.name.slice(1)
          }}:</label
        >
        <input
          type="checkbox"
          v-model="state.value"
          @change="onClientSelected()"
        />
      </div>
    </div>
    <Table
      :loading="loading"
      :items="projects"
      :defaultColumn="2"
      :defaultAscending="true"
      :href="'/projects/detail/'"
      css="60px 60px 1fr 80px 80px 40px auto"
      :limit="50"
      :pageable="true"
      :columns="[
        {
          name: 'State',
          sort: 'alphabet',
          path: ['state'],
        },
        {
          name: 'Icon',
          sort: 'alphabet',
          path: ['icon'],
        },
        {
          name: 'Project',
          sort: 'alphabet',
          path: ['name'],
        },
        {
          name: 'Created',
          sort: 'date',
          path: ['createdDate'],
          formatType: 'date',
          format: 'DD.MM.YYYY',
        },
        {
          name: 'Deadline',
          sort: 'date',
          path: ['deadline'],
          formatType: 'date',
          format: 'DD.MM.YYYY',
        },
        {
          name: 'Vimeo',
          sort: 'alphabet',
          path: ['links'],
          formatType: 'link',
          hideText: true,
          formatMap: (links) => (links.length > 0 ? links[0].url : null),
        },
        {
          name: 'Info',
          href: '/projects/pricelists/',
          appendId: true,
        },
      ]"
    >
      <template v-slot:Info="{ item }">
        <div class="icon-square" v-bind:class="getIconClass(item)">
          <i class="fas fa-euro-sign"></i>
        </div>
      </template>
      <template v-slot:Vimeo="{ item }">
        <div v-if="item.links.length > 0" class="icon-square">
          <i class="fas fa-link"></i>
        </div>
      </template>
    </Table>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import Table from "@/components/Table.vue";
import moment from "moment";
import constants from "@/constants.js";
import Fuse from "fuse.js";

export default {
  data() {
    return {
      projects: [],
      selectedClient: "all",
      states: constants.projectState.map((state) => {
        return {
          name: state,
          value: state != "canceled" && state != "finished" ? true : false,
        };
      }),
      clients: [],
      search: "",
      searchDebounceTimer: null,
      lastSearchRequestId: 0,
      loading: true,
    };
  },
  components: {
    Table,
  },
  watch: {
    search() {
      this.onClientSelected();
    },
    selectedClient() {
      this.onClientSelected();
    },
  },
  methods: {
    ...mapActions(["getClients", "getProjectSearch"]),
    updateProjects() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      // Debounce: wait 300ms after user stops typing
      this.searchDebounceTimer = setTimeout(() => {
        const requestId = ++this.lastSearchRequestId;
        // console.log("Searching...", requestId);

        this.loading = true;
        this.getProjectSearch({
          search: this.search,
          client: this.selectedClient,
          state: this.states
            .filter((state) => state.value == true)
            .map((state) => state.name),
        })
          .then((projects) => {
            // Only update if this is still the latest request
            if (requestId === this.lastSearchRequestId) {
              this.loading = false;
              this.projects = projects;
            } else {
              // console.log("Ignoring outdated response", requestId);
            }
          })
          .catch((error) => {
            if (requestId === this.lastSearchRequestId) {
              this.loading = false;
              console.log(error);
            }
          });
      }, 300); // Wait 300ms after user stops typing
    },
    onClientSelected() {
      let params = new URLSearchParams({
        client: this.selectedClient,
        query: this.search,
      });
      for (const state of this.states) {
        params.append(state.name, state.value);
      }
      this.$router.push("/projects?" + params.toString());
      this.updateProjects();
    },
    getIconClass(project) {
      if (project.selectedPricelists.length > 0) {
        return "green";
      }
      if (project.pricelists.length > 0) {
        return "yellow";
      }
      return "red";
    },
  },
  mounted() {
    if (this.$route.query.client) {
      this.selectedClient = this.$route.query.client;
    }

    if (this.$route.query.query) {
      this.search = this.$route.query.query;
    }

    for (const [i, value] of this.states.entries()) {
      if (this.$route.query[value.name] !== undefined) {
        this.states[i].value = this.$route.query[value.name] === "true";
      }
    }

    this.getClients()
      .then((clients) => {
        this.clients = clients;
        this.clients.sort((a, b) => a.name.localeCompare(b.name, "cs"));
      })
      .catch((error) => {
        console.log(error);
      });

    this.updateProjects();
  },
};
</script>

<style lang="scss" scoped>
select.fixed {
  width: 160px;
}

a,
label,
select,
input {
  align-self: center;
}

.icon-square {
  padding: 5px;
  border-radius: 3px;
}

.icon-square.green {
  background-color: #42b983;

  i {
    color: white;
  }
}

.icon-square.red {
  background-color: #e45343;

  i {
    color: white;
  }
}

.icon-square.yellow {
  background-color: orange;

  i {
    color: white;
  }
}
</style>
